import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Platform, ScrollView, View, ViewStyle } from 'react-native'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import { runOnJS } from 'react-native-reanimated'

import styled, { useTheme } from 'styled-components/native'

import { INTEGRATED_BENEFITS_SECTION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'

import { useScrollHandlers } from './useScrollHandlers'
import { BaseButton, ButtonModifier, ButtonSize, ButtonType } from '../../atoms/baseButton/BaseButton'
import { IS_WEB } from '../../constants'
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface ContentSectionData {
  id: string
  name: string
  content: React.ReactNode
  benefitSectionType: INTEGRATED_BENEFITS_SECTION_TYPE
}

export interface ContentSectionsWithNavProps {
  sections: ContentSectionData[]
  trackContentSectionNavEvent: (benefitSectionType: INTEGRATED_BENEFITS_SECTION_TYPE) => void
  /** Whether to enable the scroll-based navigation */
  isEnabled?: boolean
  testId?: string
  onSectionChange?: (sectionId: string) => void
  scrollViewRef?: React.RefObject<ScrollView>
  contentContainerStyle?: ViewStyle
  backgroundColor?: string
}

interface ContentSectionsWithNavReturn {
  NavigationLinks: React.ReactElement | null
  ContentSections: React.ReactElement
}

const LinksContainer = styled(ScrollView)<{ theme: ThemeType; backgroundColor?: string }>(
  ({ theme, backgroundColor }) => ({
    paddingVertical: theme.spacing['16px'],
    paddingHorizontal: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['48px'],
    ...(!theme.breakpoints.isMobileSized && {
      marginHorizontal: `-${theme.spacing['16px']}`,
    }),
    backgroundColor,
  }),
)

const LinkButton = styled(BaseButton)<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['8px'],
  height: '40px',
  paddingHorizontal: theme.spacing['16px'],
}))

const Spacer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['24px'] : theme.spacing['40px'],
}))

const ContentContainer = styled.View<{ theme: ThemeType; contentContainerStyle?: any }>(({ theme }) => ({
  gap: theme.spacing['64px'],
}))

interface SectionLinkButtonProps {
  section: ContentSectionData
  testId: string
  isActive: boolean
  onPress: (sectionId: string, benefitSectionType: INTEGRATED_BENEFITS_SECTION_TYPE) => void
  linksRefs: React.MutableRefObject<Map<string, any | null>>
  useGestureHandler: boolean
}

/**
 * On Android, stickyHeaderIndices uses a translateY transform that causes
 * RN Pressable to miscalculate touch-up coordinates, preventing onPress from
 * firing. Wrapping the button in a RNGH GestureDetector with Gesture.Tap()
 * works around this since RNGH handles transforms correctly.
 * https://github.com/facebook/react-native/issues/51763
 */
const SectionLinkButton: React.FC<SectionLinkButtonProps> = ({
  section,
  testId,
  isActive,
  onPress,
  linksRefs,
  useGestureHandler,
}) => {
  const { isAuroraTheme } = useTheme() as ThemeType
  const tapGesture = useMemo(
    () =>
      Gesture.Tap().onEnd(() => {
        runOnJS(onPress)(section.id, section.benefitSectionType)
      }),
    [onPress, section.id, section.benefitSectionType],
  )

  const button = (
    <LinkButton
      testID={tID(`${testId}-${section.id}Link`)}
      text={section.name}
      onPress={() => onPress(section.id, section.benefitSectionType)}
      buttonType={isAuroraTheme ? ButtonType.WORDCLOUD : ButtonType.SECONDARY}
      size={ButtonSize.SMALL}
      modifier={isAuroraTheme ? ButtonModifier.DEFAULT : ButtonModifier.OATMEAL}
      customTextSize={BodyTextSize.DEFAULT}
      selected={isActive}
    />
  )

  return (
    <View ref={(ref) => linksRefs.current.set(section.id, ref)}>
      {useGestureHandler ? <GestureDetector gesture={tapGesture}>{button}</GestureDetector> : button}
    </View>
  )
}

/**
 * - Must be wrapped in a ScrollView
 * - Set `onScroll` of the `ScrollView` to: `(scrollViewRef.current as any)?.handleContentScroll`
 * To enable sticky header, set stickyHeaderIndices on the ScrollView. If any overflow props are set on the ScrollView, stickyHeaderIndices will not work, so make sure to set them to 'unset'
 */
export const ContentSectionsWithNav = ({
  sections,
  isEnabled = true,
  testId = 'ContentSectionWithNav',
  onSectionChange,
  scrollViewRef,
  contentContainerStyle,
  backgroundColor,
  trackContentSectionNavEvent,
}: ContentSectionsWithNavProps): ContentSectionsWithNavReturn => {
  const sectionsRefs = useRef(new Map<string, View | null>())
  const linksContainerRef = useRef<ScrollView>(null)
  const linksContainerHeight = useRef(0)
  const linksRefs = useRef(new Map<string, any | null>())
  const LINKS_SCROLL_OFFSET = 8

  const [activeSectionId, setActiveSectionId] = useState<string | null>(sections[0]?.id ?? null)
  const { scrollToSection, handleScroll, scrollToLinkButton } = useScrollHandlers({
    sectionsRefs,
    scrollViewRef,
    linksRefs,
    linksContainerRef,
    linksContainerHeight,
    linksScrollOffset: LINKS_SCROLL_OFFSET,
    sections,
    isEnabled,
    setActiveSectionId,
    onSectionChange,
  })

  const debouncedHandleScroll = useDebouncedCallback(() => {
    handleScroll()
  }, 200)

  useEffect(() => {
    if (IS_WEB && isEnabled) {
      window.addEventListener('scroll', debouncedHandleScroll)
      return () => window.removeEventListener('scroll', debouncedHandleScroll)
    }
    return
  }, [debouncedHandleScroll, isEnabled])

  useEffect(() => {
    if (scrollViewRef?.current) {
      ;(scrollViewRef.current as any).handleContentScroll = handleScroll
    }
  }, [scrollViewRef, handleScroll])

  useEffect(() => {
    if (activeSectionId) {
      scrollToLinkButton(activeSectionId)
    }
  }, [activeSectionId, scrollToLinkButton])

  const handleLinkPress = useCallback(
    (sectionId: string, benefitSectionType: INTEGRATED_BENEFITS_SECTION_TYPE) => {
      setActiveSectionId(sectionId)
      scrollToSection(sectionId)
      trackContentSectionNavEvent(benefitSectionType)
    },
    [scrollToSection, setActiveSectionId, trackContentSectionNavEvent],
  )

  const isAndroid = Platform.OS === 'android'

  const NavigationLinks = isEnabled ? (
    <LinksContainer
      testID={tID(`${testId}-Links`)}
      horizontal
      ref={linksContainerRef}
      onLayout={(event) => {
        linksContainerHeight.current = event.nativeEvent.layout.height
      }}
      showsHorizontalScrollIndicator={IS_WEB}
      backgroundColor={backgroundColor}
    >
      {sections.map((section) => (
        <SectionLinkButton
          key={section.id}
          section={section}
          testId={testId}
          isActive={activeSectionId === section.id}
          onPress={handleLinkPress}
          linksRefs={linksRefs}
          useGestureHandler={isAndroid}
        />
      ))}
    </LinksContainer>
  ) : null

  const ContentSections = (
    <>
      <Spacer />
      <ContentContainer style={contentContainerStyle}>
        {sections.map((section) => (
          <View
            key={section.id}
            testID={tID(`${testId}-${section.id}Section`)}
            ref={(ref) => sectionsRefs.current.set(section.id, ref)}
          >
            {section.content}
          </View>
        ))}
      </ContentContainer>
    </>
  )

  return { NavigationLinks, ContentSections }
}
